import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, lastValueFrom, map, tap } from 'rxjs';
import { EditorService } from '../components/case-definition/alpha-json-schema-editor/alpha-json-schema-editor.service';
import { CONFIGS } from '../utils/config.constants';
import { AlphaHttpClient } from './alpha-http-client';
import { ProjectService } from './project.service';

export interface ILanguageConfig {
    id: number;
    translations: ILanguage[];
    version: string;
}
export interface ILanguage {
    languageName: string;
    languageKey: string;
    id: number;
    isDefault: boolean;
    dir: 'rtl' | 'ltr';
    langTranslations?: Record<string, string>;
    version?: string;
}
@Injectable({ providedIn: 'root' })
export class LanguagesService {
    private httpClient = inject(AlphaHttpClient);
    private projectService = inject(ProjectService);
    private editorService = inject(EditorService);

    public selectedLanguage: ILanguage | undefined;
    public languageList: ILanguage[] = [];
    private defaultLanguageSubject = new BehaviorSubject<ILanguage | null>(null);
    defaultLanguage$ = this.defaultLanguageSubject.asObservable();
    private languageListSource = new BehaviorSubject<ILanguage[]>([]);
    languageList$ = this.languageListSource.asObservable();

    // To keep updated version information of the side nav data
    languageConfigVersionData!: ILanguageConfig;
    private domainUrl = {
        type: 'environment',
        value: {
            __alpha_env__: 'ALPHA_UI_CONFIG_SERVICE_URL',
        },
    };
    coKeys: string[] = [];
    languageKeys: string[] = [];
    defaultLanguageKeys: string[] = [];

    fetchLanguages() {
        const project = this.projectService.fetchCurrentProject();
        const projectId = project?.projectId;
        return this.httpClient
            .post<ILanguageConfig>(this.domainUrl, `/language-translation/fetch-languages`, {
                projectId,
            })
            .pipe(
                tap((languageConfig) => {
                    if (languageConfig.translations.length) {
                        const defaultLanguage =
                            languageConfig.translations.find((lang) => lang.isDefault) ||
                            languageConfig.translations[0];
                        this.setLanguage(defaultLanguage);
                        this.languageConfigVersionData = languageConfig;
                        this.languageList = languageConfig.translations;
                        this.languageKeys = Object.keys(languageConfig.translations[0]?.langTranslations || {});
                        this.languageListSource.next(languageConfig.translations);
                    }
                }),
            );
    }

    addLanguage({ languageName, languageKey, langTranslations = {}, isDefault = false, dir }: ILanguage) {
        const project = this.projectService.fetchCurrentProject();
        const projectId = project?.projectId;
        return this.httpClient.post<ILanguage>(this.domainUrl, `/language-translation/create`, {
            projectId,
            languageKey,
            languageName,
            langTranslations,
            dir,
            isDefault,
        });
    }

    updateTranslations(data: ILanguage[]) {
        const project = this.projectService.fetchCurrentProject();
        const projectId = project?.projectId;
        return this.httpClient.post<ILanguage>(this.domainUrl, `/language-translation/update-translations`, {
            projectId,
            language_translations: data,
            version: this.languageConfigVersionData.version,
            configType: CONFIGS.TRANSLATION,
        });
    }

    async updateAndFetch(data: ILanguage[]) {
        await lastValueFrom(this.updateTranslations(data));
        return lastValueFrom(this.fetchLanguages());
    }

    setLanguage(language: ILanguage) {
        this.defaultLanguageSubject.next(language);
    }

    clearLanguage() {
        localStorage.removeItem('currentLanguage');
    }

    fetchCoKeys() {
        this.editorService.getSchema().then((caseDefObs) => {
            caseDefObs.subscribe({
                next: (data: any) => {
                    if (data) {
                        this.coKeys = [];
                        this.editorService.generateJsonataPaths(data, '').map((jsonPath) => {
                            this.coKeys.push(jsonPath.path);
                        });
                    }
                },
            });
        });
    }

    fetchDefaultTranslationKeys(): Promise<void> {
        const project = this.projectService.fetchCurrentProject();
        const projectId = project?.projectId;

        return lastValueFrom(
            this.httpClient
                .post<
                    Record<string, string>
                >(this.domainUrl, `/language-translation/default-translations`, { projectId })
                .pipe(
                    map((data) => {
                        if (data && Object.keys(data).length) {
                            this.defaultLanguageKeys = Object.keys(data);
                        }
                    }),
                ),
        );
    }
}
